import { FieldProps } from "formik";
import React, { FormEvent, useEffect, useRef, PropsWithChildren } from "react";
import { cn } from "@/lib/utils";
import useShareForwardedRef from "@/helpers/useSharedRef";

interface TextareaExtraProps {
  autoResize?: boolean;
  placeholder?: string;
  maxHeight?: number;
}

type TextAreaProps = React.HTMLProps<HTMLTextAreaElement> & TextareaExtraProps;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ autoResize, rows = 1, maxHeight, ...restProps }, forwardedRef) => {
    const ref = useShareForwardedRef<HTMLTextAreaElement>(forwardedRef);

    useEffect(() => {
      if (autoResize) {
        setTimeout(() => {
          if (ref.current) {
            // this line is used to correct the height of textArea field when user delete several strings to avoid empty space
            ref.current.style.cssText = "height:auto;";

            if (maxHeight && maxHeight < ref.current.scrollHeight) {
              ref.current.style.cssText = `height: ${maxHeight}px;`;
              ref.current.style.overflowY = "auto";
              return;
            }

            ref.current.style.cssText = `height: ${ref.current.scrollHeight}px;`;
          }
        }, 0);
      }
    }, [restProps.value, autoResize, ref, maxHeight]);

    return <textarea {...restProps} ref={ref} rows={rows} />;
  },
);

interface TextareaFieldBaseProps {
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  emojiClassName?: string;
  containerClassName?: string;
  label?: string;
  onChange?: (event: FormEvent<HTMLTextAreaElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent) => void;
  autoHeight?: boolean;
  fullWidth?: boolean;
  textareaClassName?: string;
  id?: string;
  disabled?: boolean;
  emoji?: boolean;
  maxWidth?: number;
  maxLength?: number;
  rows?: number;
  maxHeight?: number;
  showRequiredErrors?: boolean;
}

type TextareaFieldProps = TextareaFieldBaseProps &
  TextareaExtraProps &
  FieldProps;

export const TextareaField: React.FC<PropsWithChildren<TextareaFieldProps>> = ({
  field,
  label,
  form: { touched, errors },
  children,
  inputClassName,
  labelClassName,
  emojiClassName,
  fullWidth,
  textareaClassName,
  emoji,
  maxWidth,
  maxLength,
  showRequiredErrors = true,
  ...props
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  let fieldErrors;
  if (errors[field.name]) {
    // errors[field.name] can be string or array, so converting it to array in case of string.
    if (typeof errors[field.name] === "string") {
      fieldErrors = [errors[field.name]];
    } else {
      fieldErrors = errors[field.name];
    }
    fieldErrors = (fieldErrors as string[]).filter((v) => !!v);
  }
  return (
    <div>
      <div className={cn(props.containerClassName)}>
        {label && (
          <label
            htmlFor={field.name}
            className={cn(labelClassName)}
            id={field.name}
          >
            {label}
          </label>
        )}
        <div>
          <Textarea
            {...field}
            {...props}
            maxLength={maxLength}
            ref={ref}
            className={cn(inputClassName, textareaClassName)}
          />
        </div>
      </div>
      {touched[field.name] &&
        fieldErrors &&
        !!fieldErrors.length &&
        showRequiredErrors && (
          <div className={cn(props.errorClassName)}>{fieldErrors[0]}</div>
        )}
      {maxLength && (
        <div className="text-xs font-light text-muted-foreground w-fit ml-auto">
          <span>*{maxLength} characters max</span>
        </div>
      )}
    </div>
  );
};
